import React from 'react';
import { Link } from '@/components/LocalizedLink';
import { ArrowRight, Clock, Tag, Camera } from 'lucide-react';
import { motion } from 'framer-motion';
import { useApiData } from '@/hooks/useApiData';

const NO_ITEMS = [];
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

export default function BlogPage() {
  const { data: posts, loading } = useApiData('/api/blog', NO_ITEMS);

  return (
    <div data-testid="blog-page">

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-saffron-50/50 to-cream" />
        <div className="relative max-w-7xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.7 }}>
            <span className="text-xs uppercase tracking-[0.3em] text-saffron font-sans font-semibold">Insights & Guides</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-earth-dark mt-3 tracking-tight">
              Spice Export <span className="text-saffron">Blog</span>
            </h1>
            <p className="mt-4 text-lg text-earth/60 font-sans max-w-xl leading-relaxed">
              Industry knowledge, quality guides, and export insights for global spice buyers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20 text-earth/30 font-sans">Loading posts...</div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-earth/40 font-sans text-lg">No posts yet</p>
              <p className="text-earth/30 font-sans text-sm mt-1">Check back soon for expert spice export guides.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, i) => (
                <motion.div
                  key={post.slug}
                  initial="hidden" whileInView="visible" viewport={{ once: true }}
                  variants={fadeUp} transition={{ delay: i * 0.1, duration: 0.5 }}
                >
                  <Link
                    to={`/blog/${post.slug}`}
                    className="group block bg-white rounded-lg overflow-hidden shadow-card-lift hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 border border-saffron-100/50 h-full"
                    data-testid={`blog-card-${post.slug}`}
                  >
                    <div className="relative aspect-[16/9] overflow-hidden">
                      {post.cover_image ? (
                        <img src={post.cover_image} alt={post.title} width={800} height={450} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-saffron-50/60 to-cream flex items-center justify-center">
                          <Camera size={32} className="text-earth/15" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="flex items-center gap-1 text-[10px] text-earth/40 font-sans">
                          <Clock size={10} />
                          {new Date(post.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        {post.tags?.length > 0 && (
                          <span className="flex items-center gap-1 text-[10px] text-saffron font-sans uppercase tracking-wider">
                            <Tag size={10} /> {post.tags[0]}
                          </span>
                        )}
                      </div>
                      <h2 className="font-display text-lg font-bold text-earth-dark group-hover:text-saffron transition-colors leading-tight" data-testid={`blog-title-${post.slug}`}>
                        {post.title}
                      </h2>
                      <p className="mt-2 text-sm text-earth/50 font-sans leading-relaxed line-clamp-3">{post.excerpt}</p>
                      <div className="mt-4 flex items-center gap-1 text-saffron font-sans text-xs font-semibold group-hover:gap-2 transition-all">
                        Read Article <ArrowRight size={12} />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SEO Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-saffron-50/30">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-2xl font-bold text-earth-dark mb-4">Expert Spice Export Insights</h2>
          <p className="text-sm text-earth/60 font-sans leading-relaxed">
            Stay updated with the latest from India's spice export industry. Our blog covers quality parameters, import regulations, market trends, and sourcing guides — helping global buyers make informed decisions when choosing a <strong>spice trading company from Maharashtra</strong>.
          </p>
        </div>
      </section>
    </div>
  );
}
